import { queryOptions, useSuspenseQuery } from '@tanstack/react-query';
import axios from 'axios';
import { z } from 'zod';
import {
    PointGeometry,
    pointGeometrySchema,
} from 'app/agriculture/queries/types/geometry/PointGeometry';
import { handleZodParseError } from 'app/agriculture/utils';

const OpenStreetMapResultSchema = z.object({
    geometry: pointGeometrySchema,
    bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
    properties: z
        .object({ place_id: z.number(), osm_id: z.number(), display_name: z.string() })
        .transform(({ place_id, osm_id, display_name }) => ({
            placeId: place_id,
            OSMId: osm_id,
            displayName: display_name,
        })),
});

export interface OpenStreetMapResult {
    geometry: PointGeometry;
    bbox: [number, number, number, number];
    properties: { placeId: number; OSMId: number; displayName: string };
}

interface QueryParams {
    format: 'geojson';
    q: string;
}

export function useOpenStreetMapQuery(searchTerm: string) {
    return useSuspenseQuery(
        queryOptions({
            queryKey: [
                'https://nominatim.openstreetmap.org/search',
                { format: 'geojson', q: searchTerm },
            ],
            queryFn: async ({
                queryKey: [endpoint, params],
            }: {
                queryKey: [endpoint: string, params: QueryParams];
            }) => {
                if (params.q === '') return [];
                const { data } = await axios.get<unknown>(endpoint, { params });

                try {
                    return z.object({ features: z.array(OpenStreetMapResultSchema) }).parse(data)
                        .features as OpenStreetMapResult[];
                } catch (e) {
                    handleZodParseError(e);
                    throw e;
                }
            },
        }),
    ).data;
}
